/**
 * <s-float-canvas> — gives the homepage canvas its gentle cursor drift.
 * Tracks the pointer across the whole viewport and eases --mx / --my (-1..1)
 * on itself; each shirt turns those into its own translate + tilt using its
 * --shirt-depth, so shirts move at different speeds and directions.
 * Skipped on touch screens (shirts idle-float instead) and for reduced motion.
 */
class SFloatCanvas extends HTMLElement {
  #ac;
  #raf = 0;
  #target = { x: 0, y: 0 };
  #current = { x: 0, y: 0 };

  connectedCallback() {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!fine.matches || reduced.matches) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };

    window.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      this.#target.x = (event.clientX / window.innerWidth) * 2 - 1;
      this.#target.y = (event.clientY / window.innerHeight) * 2 - 1;
      this.#start();
    }, opts);

    document.documentElement.addEventListener('pointerleave', () => {
      this.#target.x = 0;
      this.#target.y = 0;
      this.#start();
    }, opts);

    reduced.addEventListener('change', () => {
      if (reduced.matches) this.#stop(true);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stop(false);
  }

  #start() {
    if (!this.#raf) this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop(reset) {
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    if (reset) {
      this.style.removeProperty('--mx');
      this.style.removeProperty('--my');
    }
  }

  #tick = () => {
    // Ease toward the pointer so motion feels soft and never jittery.
    this.#current.x += (this.#target.x - this.#current.x) * 0.08;
    this.#current.y += (this.#target.y - this.#current.y) * 0.08;
    this.style.setProperty('--mx', this.#current.x.toFixed(4));
    this.style.setProperty('--my', this.#current.y.toFixed(4));

    const settled = Math.abs(this.#target.x - this.#current.x) < 0.001
      && Math.abs(this.#target.y - this.#current.y) < 0.001;
    this.#raf = settled ? 0 : requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('s-float-canvas')) {
  customElements.define('s-float-canvas', SFloatCanvas);
}
